import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listComuni } from '@/api/endpoints';
import type { ComuneSintesi, Paginated } from '@/api/types';
import { Card, CardGrid } from '@/ui/Card/Card';
import { TextField } from '@/ui/Field/Field';
import { FieldValue } from '@/ui/Absence/Absence';
import { Breadcrumb } from '@/ui/Breadcrumb/Breadcrumb';
import { useAsync, useDocumentTitle } from '@/lib/hooks';
import { messaggioErrore } from '@/lib/erroreUtente';
import { PROVINCE } from '@/lib/province';
import styles from './ComuniIndexPage.module.css';

/**
 * Indice dei 257 comuni, raggruppati per provincia.
 *
 * Impianto grafico dal riferimento (visual-diff pagine 31/07/2026): colonna
 * 1280px, h1 56px navy, ricerca su velo navy al 4%, quicknav provinciale a
 * chip-àncora e SEI SEZIONI, una per provincia, con intestazione
 * «SIGLA · Provincia di X · (n)» e id `prov-<slug>` (voce 9, decisione
 * utente del 31/07/2026 — le chip della quicknav sono ora àncore, come sul
 * riferimento; la select provincia non serve più: le sezioni la assorbono).
 *
 * La ricerca resta client-side sui dati già caricati (257 record, ~40 KB) e
 * filtra DENTRO le sezioni: una provincia senza risultati non si mostra
 * mentre si cerca (è il filtro al lavoro, non un buco nei dati). Il numero
 * di risultati è annunciato in una regione live (WCAG 4.1.3): senza, chi usa
 * uno screen reader digita nel campo e non sa se sta trovando qualcosa.
 */
export function ComuniIndexPage() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('comuni_title', 'Tutti i Comuni'));

  const [query, setQuery] = useState('');

  const state = useAsync<Paginated<ComuneSintesi>>(
    // `per_pagina` alto: i 257 comuni stanno in una pagina sola e il filtro
    // resta client-side, senza una richiesta a ogni carattere digitato.
    (signal) => listComuni({ signal, language: i18n.language, per_pagina: 300 }),
    [i18n.language],
  );

  // La busta del backend è `{meta, risultati}`, non `{count, results}`.
  const all = state.status === 'success' ? state.data.risultati : [];

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((comune) => comune.nome.toLowerCase().includes(needle));
  }, [all, query]);

  /* I comuni per provincia, GIÀ filtrati dalla ricerca: l'ordine delle
     sezioni è quello amministrativo di `PROVINCE`, non quello dei dati. */
  const perProvincia = useMemo(() => {
    const mappa = new Map<string, ComuneSintesi[]>();
    for (const comune of filtered) {
      const lista = mappa.get(comune.provincia) ?? [];
      lista.push(comune);
      mappa.set(comune.provincia, lista);
    }
    return mappa;
  }, [filtered]);

  return (
    <div className={styles.wrapper}>
      {/* voce 7: breadcrumb «Home › Tutti i Comuni» in testa al contenuto. */}
      <Breadcrumb
        className={styles.breadcrumb}
        items={[
          { label: t('breadcrumb_home', 'Home'), to: '/' },
          { label: t('comuni_title', 'Tutti i Comuni') },
        ]}
      />

      <h1 className={styles.title}>{t('comuni_title', 'Tutti i Comuni')}</h1>
      <p className={styles.lead}>
        {t(
          'comuni_sub',
          'Esplora i 257 comuni pugliesi. Clicca su un comune per vedere servizi, contatti ed eventi.',
        )}
      </p>

      {/* `role="search"` rende il gruppo trovabile fra i landmark. */}
      <div className={styles.filters} role="search">
        <TextField
          label={t('search_button', 'Cerca')}
          placeholder={t('search_placeholder', 'Cerca per nome del comune o CAP')}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {/* voce 8: quicknav provinciale — sigla in badge scuro + nome esteso.
          Con le sezioni della voce 9 le chip sono ÀNCORE a `#prov-<slug>`,
          come sul riferimento. */}
      <nav className={styles.quicknav} aria-label={t('nav_province', 'Province')}>
        <ul className={styles.quicknavList}>
          {PROVINCE.map((provincia) => (
            <li key={provincia.sigla}>
              <a className={styles.chip} href={`#prov-${provincia.slug}`}>
                <span className={styles.chipSigla} aria-hidden="true">
                  {provincia.sigla}
                </span>
                {provincia.nome}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Il conteggio dei risultati va annunciato: è l'unico riscontro che la
          ricerca sta funzionando per chi non vede la griglia aggiornarsi. */}
      <p className={styles.count} role="status" aria-live="polite">
        {state.status === 'loading'
          ? t('loading', 'Caricamento…')
          : t('search_results_count', {
              count: filtered.length,
              defaultValue: `${filtered.length} comuni`,
            })}
      </p>

      {state.status === 'error' ? (
        <p role="alert">
          <strong>{messaggioErrore(state.error, t).titolo}</strong> —{' '}
          {messaggioErrore(state.error, t).testo}
        </p>
      ) : (
        PROVINCE.map((provincia) => {
          const comuni = perProvincia.get(provincia.sigla) ?? [];
          /* Con una ricerca attiva, una provincia senza corrispondenze non
             si mostra: è il filtro dell'utente al lavoro, non un'assenza
             nei dati. Senza ricerca le sezioni ci sono sempre. */
          if (comuni.length === 0 && query.trim()) return null;
          const nomeProvincia = `${t(
            'comune_hero_badge_province_prefix',
            'Provincia di',
          )} ${provincia.nome}`;
          return (
            <section
              key={provincia.sigla}
              id={`prov-${provincia.slug}`}
              className={styles.sezioneProvincia}
              aria-labelledby={`prov-titolo-${provincia.slug}`}
            >
              {/* voce 74 (adottata con la voce 9): titolo di sezione a
                  24px/700, nel formato del riferimento «SIGLA · Provincia
                  di X · (n)». */}
              <h2 id={`prov-titolo-${provincia.slug}`} className={styles.sectionTitle}>
                <span className={styles.chipSigla} aria-hidden="true">
                  {provincia.sigla}
                </span>{' '}
                {nomeProvincia}{' '}
                <span className={styles.sectionCount}>({comuni.length})</span>
              </h2>
              <CardGrid label={nomeProvincia} className={styles.griglia}>
                {comuni.map((comune) => (
                  <li key={comune.slug}>
                    <Card
                      title={comune.nome}
                      to={`/comuni/${comune.slug}/`}
                      headingLevel={3}
                    >
                      <p>
                        {t('comune_hero_meta_population', 'Popolazione')}:{' '}
                        <FieldValue
                          value={comune.popolazione}
                          render={(v) => Number(v).toLocaleString('it-IT')}
                        />
                      </p>
                    </Card>
                  </li>
                ))}
              </CardGrid>
            </section>
          );
        })
      )}
    </div>
  );
}
